// src/pages/business/Memberships/list/hooks/useMembershipDetails.js
import { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import MembershipsService from '@/services/MembershipsService'
import ProductsService from '@/services/ProductsService'

export default function useMembershipDetails(id, navigate) {
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [membership, setMembership] = useState(null)

	const [productsLoading, setProductsLoading] = useState(false)
	const [products, setProducts] = useState([])

	const toDate = v => {
		if (!v) return null
		if (v instanceof Date) return v
		const s = String(v).includes(' ') ? String(v).replace(' ', 'T') : String(v)
		const d = new Date(s)
		return isNaN(d.getTime()) ? null : d
	}
	const ymd = d => {
		const x = toDate(d)
		if (!x) return ''
		const y = x.getFullYear()
		const m = String(x.getMonth() + 1).padStart(2, '0')
		const da = String(x.getDate()).padStart(2, '0')
		return `${y}-${m}-${da}`
	}
	const getExpiryEndOfDay = m => {
		const raw = m?.expiryDate || m?.expiry || m?.expirydate
		if (!raw) return null
		const str = String(raw)
		if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
			const [y, mo, da] = str.split('-').map(Number)
			return new Date(y, mo - 1, da, 23, 59, 59, 999)
		}
		return toDate(str)
	}
	const extractIds = m => {
		const cid =
			(typeof m?.customerId === 'object'
				? m?.customerId?._id || m?.customerId?.id || m?.customerId?.customerid
				: m?.customerId || m?.customerid) || ''
		const cname = (m?.customerName || (typeof m?.customerId === 'object' ? m?.customerId?.name : '') || '').trim()
		return { cid: String(cid), cname }
	}
	const buildUpdatePayload = (m, overrides = {}) => {
		const { cid, cname } = extractIds(m)
		const p = new URLSearchParams()
		p.append('customerName', cname)
		p.append('customerId', cid)
		p.append('type', String(m?.type || '').toLowerCase())
		p.append('status', overrides.status ?? String(m?.status || 'active').toLowerCase())
		p.append('remainingUsage', String(m?.remainingUsage ?? 0))
		const expStr = ymd(m?.expiryDate || m?.expiry || m?.expirydate)
		if (expStr) p.append('expiryDate', expStr)
		return p
	}

	const ensureExpiredIfNeeded = useCallback(async (m, usedAt = null) => {
		const exp = getExpiryEndOfDay(m)
		if (!exp) return
		const ref = usedAt || new Date()
		const mustExpire = ref.getTime() > exp.getTime()
		if (mustExpire && String(m?.status || '').toLowerCase() !== 'expired') {
			const payload = buildUpdatePayload(m, { status: 'expired' })
			await MembershipsService.updateMembership(m._id, payload)
			const fresh = await MembershipsService.getMembershipById(m._id)
			if (!fresh.error && fresh.data) setMembership(fresh.data)
		}
	}, [])

	const loadMembership = useCallback(async () => {
		setLoading(true)
		const { data, error } = await MembershipsService.getMembershipById(id)
		if (error) setError(error)
		else {
			setMembership(data)
			await ensureExpiredIfNeeded(data)
		}
		setLoading(false)
	}, [id, ensureExpiredIfNeeded])

	const loadProducts = useCallback(async () => {
		setProductsLoading(true)
		const { data, error } = await ProductsService.getAllProducts()
		if (!error) setProducts(Array.isArray(data) ? data : [])
		setProductsLoading(false)
	}, [])

	useEffect(() => {
		loadMembership()
	}, [loadMembership])

	useEffect(() => {
		loadProducts()
	}, [loadProducts])

	const handleConsume = useCallback(
		async ({ membershipId, date, service }) => {
			const isExpired = String(membership?.status || '').toLowerCase() === 'expired'
			if (isExpired) {
				return { success: false, error: 'expired' }
			}

			const usedAt = toDate(date) || new Date()
			const res = await MembershipsService.consumeMembership(membershipId, { date, service })
			if (!res.success) return { success: false, error: res.error }

			const fresh = await MembershipsService.getMembershipById(membershipId)
			if (!fresh.error && fresh.data) {
				setMembership(fresh.data)
				await ensureExpiredIfNeeded(fresh.data, usedAt)
			}
			return { success: true }
		},
		[membership, ensureExpiredIfNeeded]
	)

	const handleDelete = useCallback(async () => {
		if (!membership?._id) return
		const result = await Swal.fire({
			title: 'Are you sure?',
			text: `You are about to delete membership for "${membership.customerName}". This action cannot be undone!`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Yes, delete',
			cancelButtonText: 'Cancel',
		})
		if (!result.isConfirmed) return

		const { success, error } = await MembershipsService.deleteMemberships([membership._id])
		if (!success) {
			await Swal.fire({ title: 'Error', text: error || 'Failed to delete membership', icon: 'error' })
			return
		}
		await Swal.fire({ title: 'Deleted!', text: 'Membership deleted successfully.', icon: 'success' })
		navigate('/memberships', { state: { message: 'Membership deleted successfully.' } })
	}, [membership, navigate])

	return {
		loading,
		error,
		membership,
		productsLoading,
		products,
		handleDelete,
		handleConsume,
		reload: loadMembership,
	}
}
